<div class="screensaver">
    <ng-container *ngIf="images$ | async as images">
        <ng-container *ngIf="activeIndex$ | async as activeIndex">
            <div class="screensaver-bg" *ngFor="let image of images; let i = index; trackBy: trackByIndex"
                [class.active]="i === activeIndex" [class.zoom-blur]="(uiViewModel$ | async)?.unlockVisible"
                [style.backgroundImage]="'url(' + image + ')'">
            </div>
        </ng-container>
    </ng-container>

    <div class="screensaver-overlay"></div>
    <div class="screensaver-session-countdown" *ngIf="sessionRemainingLabel$ | async as sessionRemainingLabel">
        <span *ngIf="sessionRemainingLabel">{{ sessionRemainingLabel }}</span>
    </div>

    <div class="screensaver-content" [class.unlock-active]="(uiViewModel$ | async)?.unlockVisible">
        <div class="screensaver-logo">
            <div class="screensaver-logo-layer app-logo" [class.hidden]="(uiViewModel$ | async)?.showUserAvatar">
                <app-navizard-logo [usePreset]="false" [isLight]="true" [logoType]="'v1'"></app-navizard-logo>
            </div>
            <div class="screensaver-logo-layer user-avatar" [class.visible]="(uiViewModel$ | async)?.showUserAvatar" *ngIf="(uiViewModel$ | async)?.showUserAvatar && avatarUrl">
                <img class="screensaver-user-photo" [src]="avatarUrl" [alt]="displayName" (error)="onAvatarError()">
            </div>
        </div>
        <ng-container *ngIf="now$ | async as now">
            <div class="screensaver-time">
                {{ now | date:'HH:mm' }}
            </div>
            <div class="screensaver-date">
                {{ now | date:'EEEE, MMM d' }}
            </div>
        </ng-container>
        <div class="screensaver-panel" [class.visible]="(uiViewModel$ | async)?.unlockVisible">
            <ng-container *ngIf="overlayMode === 'unlock'">
                <div class="screensaver-lock-user" *ngIf="(uiViewModel$ | async)?.showUserIdentity">
                    {{ displayName }}
                </div>
                <ng-container *ngIf="hasPin; else noPinPrompt">
                    <div class="screensaver-lock-desc">
                        {{ 'SECURITY.SCREEN_SAVER_UNLOCK_DESC' | translate }}
                    </div>
                    <div class="screensaver-lock-input">
                        <app-two-factor-input #unlockInput [formControl]="pinControl" size="medium" style="compact"
                            [maxLength]="4" [autoFocus]="true" [disabled]="lockoutRemaining > 0"
                            (codeComplete)="attemptUnlock($event)">
                        </app-two-factor-input>
                    </div>
                    <div class="screensaver-lock-error" *ngIf="pinErrorKey">
                        {{ pinErrorKey | translate:{seconds: lockoutRemaining} }}
                    </div>
                    <div class="screensaver-lock-actions" *ngIf="showLogoutAction">
                        <button mat-button type="button" (click)="logout()">
                            {{ 'LOGOUT' | translate }}
                        </button>
                    </div>
                </ng-container>
                <ng-template #noPinPrompt>
                    <div class="screensaver-lock-desc">
                        {{ 'SECURITY.SCREEN_SAVER_NO_PIN_DESC' | translate }}
                    </div>
                    <div class="screensaver-lock-actions">
                        <button mat-stroked-button type="button" (click)="startSetup()">
                            {{ 'SECURITY.SCREEN_SAVER_NO_PIN_SET' | translate }}
                        </button>
                    </div>
                </ng-template>
            </ng-container>

            <ng-container *ngIf="overlayMode === 'setup'">
                <div class="screensaver-lock-desc">
                    {{ 'SECURITY.SCREEN_SAVER_PIN_DESC' | translate }}
                </div>

                <ng-container *ngIf="setupStep === 'current'">
                    <div class="screensaver-lock-label">
                        {{ 'SECURITY.SCREEN_SAVER_PIN_CURRENT' | translate }}
                    </div>
                    <div class="screensaver-lock-input">
                        <app-two-factor-input #currentPinInput [formControl]="currentPinControl" size="medium"
                            style="compact" [maxLength]="4" [autoFocus]="true"
                            (codeComplete)="proceedFromCurrent($event)">
                        </app-two-factor-input>
                    </div>
                </ng-container>

                <ng-container *ngIf="setupStep === 'new'">
                    <div class="screensaver-lock-label">
                        {{ 'SECURITY.SCREEN_SAVER_PIN_NEW' | translate }}
                    </div>
                    <div class="screensaver-lock-input">
                        <app-two-factor-input #newPinInput [formControl]="newPinControl" size="medium" style="compact"
                            [maxLength]="4" [autoFocus]="true" (codeComplete)="saveNewPin()">
                        </app-two-factor-input>
                    </div>
                </ng-container>

                <div class="screensaver-lock-actions" *ngIf="setupStep === 'new'">
                    <button mat-button type="button" (click)="cancelSetup()">
                        {{ 'SECURITY.SCREEN_SAVER_PIN_ACTION_CANCEL' | translate }}
                    </button>
                </div>

                <div class="screensaver-lock-error" *ngIf="pinErrorKey">
                    {{ pinErrorKey | translate }}
                </div>
            </ng-container>
        </div>
    </div>

    <ng-container *ngIf="tipViewModel$ | async as tipVm">
        <ng-container *ngIf="tipVm.tip as tip">
            <div class="screensaver-tips">
                <div class="screensaver-tips-media" *ngIf="tip.videoUrl || tip.coverUrl">
                    <img *ngIf="tip.coverUrl"
                        class="screensaver-tips-cover"
                        [class.is-visible]="tipVm.shouldShowCover"
                        [src]="tip.coverUrl"
                        [alt]="tip.title"
                        loading="eager">
                    <video *ngIf="tip.videoUrl"
                        #tipVideo
                        class="screensaver-tips-video"
                        [class.is-hidden]="tipVm.shouldShowCover"
                        [src]="tip.videoUrl"
                        [attr.poster]="tip.coverUrl || null"
                        autoplay
                        muted
                        playsinline
                        preload="metadata"
                        (loadeddata)="onTipVideoLoadedData($event)"
                        (canplay)="onTipVideoCanPlay($event)"
                        (play)="onTipVideoPlay($event)"
                        (ended)="onTipVideoEnded()"
                        (error)="onTipVideoError()">
                    </video>
                </div>
                <div class="screensaver-tips-body">
                    <div class="screensaver-tips-kicker">
                        Solution {{ tipVm.displayIndex }} / {{ tipVm.total }}
                    </div>
                    <div class="screensaver-tips-title">
                        {{ tip.title }}
                    </div>
                    <div class="screensaver-tips-desc">
                        {{ tip.description }}
                    </div>
                </div>
            </div>
        </ng-container>
    </ng-container>
</div>
